import { MerchantDetailsModel } from '@/models/MerchantDetailsModel';
import {
  MenuExtraListItemRawModel,
  MenuExtraListRawModel,
  MenuItemRawModel,
  MenuItemSizeRawModel,
  MenuRawModel,
  MerchantDetailsRawModel,
  OperatingHoursRawModel,
} from '@/api/api-raw-models/merchant-details-raw-model';
import { OperatingHoursModel } from '@/models/OperatingHoursModel';
import { MenuModel } from '@/models/MenuModel';
import { MenuExtraListItemModel } from '@/models/MenuExtraListItemModel';
import { MenuExtraListModel } from '@/models/MenuExtraListModel';
import { MenuItemSizeModel } from '@/models/MenuItemSizeModel';
import { MenuItemModel } from '@/models/MenuItemModel';
import { DayShort } from '@/types';

function mapOperatingHours(e: OperatingHoursRawModel): OperatingHoursModel {
  return {
    id: e.id,
    close: e.close,
    day: e.day as DayShort,
    open: e.open,
    parentId: e.parent_id,
    parentType: e.parent_type,
    createdAt: new Date(e.created_at),
    updatedAt: new Date(e.updated_at),
  };
}

function mapExtraListItem(e: MenuExtraListItemRawModel): MenuExtraListItemModel {
  return {
    id: e.id,
    extraListItemId: e.extra_list_item_id,
    itemExtraListId: e.item_extra_list_id,
    menuItemId: e.menu_item_id,
    name: e.name,
    visible: e.visible,
    price: Number(e.price).toFixed(2),
  };
}

function mapExtraList(e: MenuExtraListRawModel): MenuExtraListModel {
  return {
    id: e.id,
    menuItemId: e.menu_item_id,
    extraListId: e.extra_list_id,
    name: e.name,
    visible: e.visible,
    min: e.min,
    max: e.max,
    required: e.required,
    itemExtraListItems: e.item_extra_list_items.map(mapExtraListItem),
  };
}

function mapItemSize(e: MenuItemSizeRawModel): MenuItemSizeModel {
  return {
    id: e.id,
    name: e.name,
    price: Number(e.price).toFixed(2),
  };
}

function mapItem(e: MenuItemRawModel): MenuItemModel {
  return {
    id: e.id,
    menuId: e.menu_id,
    submenuId: e.submenu_id,
    name: e.name,
    description: e.description,
    visible: e.visible,
    sizes: e.sizes.map(mapItemSize),
    extraLists: e.extra_lists.map(mapExtraList),
    isHot: e.is_hot,
    taxRate: e.tax_rate,
  };
}

function mapMenu(m: MenuRawModel): MenuModel {
  // insert submenu if there is none exists
  const submenus = m.submenus.length ? m.submenus : [{ id: m.id, name: m.name }];

  return {
    id: m.id,
    name: m.name,
    taxRate: m.tax_rate,
    visible: m.visible,
    submenus: submenus.map((s) => ({
      id: s.id,
      name: s.name,
      items: m.items.filter((i) => (i.submenu_id ? i.submenu_id === s.id : true)).map(mapItem),
    })),
  };
}

export const mapMerchantDetails = (x: MerchantDetailsRawModel): MerchantDetailsModel => {
  const { merchant } = x;

  const operatingHours: OperatingHoursModel[] = merchant.operating_hours.map(mapOperatingHours);

  const menus: MenuModel[] = merchant.menus.map(mapMenu);

  return {
    id: merchant.id,
    active: merchant.active,
    autoOpen: merchant.auto_open,
    communicationEmail: merchant.communication_email,
    coverPhotoUrls: merchant.cover_photo_urls,
    curbsidePickup: merchant.curbside_pickup,
    fullAddress: merchant.full_address,
    latitude: merchant.latitude,
    logoUrl: merchant.logo_url,
    longitude: merchant.longitude,
    name: merchant.name,
    nextClose: merchant.next_close,
    nextOpen: merchant.next_open,
    orderAheadNumDays: merchant.order_ahead_num_days,
    phone: merchant.phone,
    pickupText: merchant.pickup_text,
    rewardsEnabled: merchant.rewards_enabled,
    state: merchant.state,
    streetAddress: merchant.street_address,
    taxRate: merchant.tax_rate,
    timezone: merchant.timezone,
    tipsEnabled: merchant.tips_enabled,
    menus,
    operatingHours,
  };
};
